import { isPlatformBrowser, DOCUMENT, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, PLATFORM_ID, Directive, Inject, Input, Output, NgModule } from '@angular/core';
import { WINDOW, WindowModule } from '@upr-web-primer/window';

let lazyLoadId = 0;
class LazyLoadDirective {
    constructor(_element, document, window, platformId) {
        this._element = _element;
        this.document = document;
        this.window = window;
        this.platformId = platformId;
        this._wait = false;
        this.lazyOn = true;
        this.marginBottom = 0;
        this.deferLoad = new EventEmitter();
    }
    get wait() {
        return this._wait;
    }
    set wait(bool) {
        this._wait = bool;
        if (!bool) {
            this.checkForObserver();
        }
    }
    ngOnInit() {
        if (!this._element.nativeElement.id) {
            this._element.nativeElement.id = `upr-lazy-load_${lazyLoadId}`;
            lazyLoadId++;
        }
        if (!this.wait) {
            this.checkForObserver();
        }
    }
    checkForObserver() {
        if (this.lazyOn &&
            isPlatformBrowser(this.platformId) &&
            'IntersectionObserver' in this.window) {
            this.registerIntersectionObserver();
            if (this._intersectionObserver && this._element.nativeElement) {
                this._intersectionObserver.observe(this._element.nativeElement);
            }
        }
        else {
            this.load();
        }
    }
    registerIntersectionObserver() {
        if (!!this._intersectionObserver) {
            return;
        }
        this._intersectionObserver = new IntersectionObserver(entries => {
            this.checkForIntersection(entries);
        }, {
            rootMargin: `0px 0px ${this.marginBottom}px 0px`,
            threshold: 0
        });
    }
    checkForIntersection(entries) {
        entries.forEach((entry) => {
            if (this.checkIfIntersecting(entry)) {
                if (this._intersectionObserver && this._element.nativeElement) {
                    this._intersectionObserver.unobserve(this._element.nativeElement);
                    this.load();
                }
            }
        });
    }
    checkIfIntersecting(entry) {
        if (entry && entry.target) {
            return ((entry.isIntersecting || entry.intersectionRatio > 0) &&
                entry.target.id === this._element.nativeElement.id);
        }
        return this.isVisible();
    }
    load() {
        this.removeListeners();
        this.deferLoad.emit();
    }
    isVisible() {
        const scrollPosition = this.getScrollPosition();
        const elementOffset = this._element.nativeElement.offsetTop ||
            this._element.nativeElement.getBoundingClientRect().top;
        return elementOffset <= scrollPosition;
    }
    getScrollPosition() {
        return ((this.window.scrollY || this.window.pageYOffset) +
            (this.document.documentElement.clientHeight ||
                this.document.body.clientHeight) +
            this.marginBottom);
    }
    removeListeners() {
        if (this._intersectionObserver) {
            this._intersectionObserver.disconnect();
        }
    }
    ngOnDestroy() {
        this.removeListeners();
    }
}
LazyLoadDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LazyLoadDirective, deps: [{ token: i0.ElementRef }, { token: DOCUMENT }, { token: WINDOW }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Directive });
LazyLoadDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: LazyLoadDirective, selector: "[uprLazyLoad]", inputs: { lazyOn: "lazyOn", wait: "wait", marginBottom: "marginBottom" }, outputs: { deferLoad: "deferLoad" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LazyLoadDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[uprLazyLoad]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; }, propDecorators: { lazyOn: [{
                type: Input
            }], wait: [{
                type: Input
            }], marginBottom: [{
                type: Input
            }], deferLoad: [{
                type: Output
            }] } });

class LazyLoadModule {
}
LazyLoadModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LazyLoadModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
LazyLoadModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LazyLoadModule, declarations: [LazyLoadDirective], imports: [CommonModule, WindowModule], exports: [LazyLoadDirective] });
LazyLoadModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LazyLoadModule, imports: [[CommonModule, WindowModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: LazyLoadModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, WindowModule],
                    declarations: [LazyLoadDirective],
                    exports: [LazyLoadDirective]
                }]
        }] });

export { LazyLoadDirective, LazyLoadModule };
